import { useEffect, useState } from 'react'
import { ArrowLeft, ChevronDown, Plus, Sparkles, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AiSuggestionChips } from './AiSuggestionChips'
import { JtbdSectionEditor } from './JtbdSectionEditor'
import { PersonaAvatarUploader } from './PersonaAvatarUploader'
import type { DecisionPower, JtbdData, PersonaData, PersonaType } from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'

interface Props {
  session: { id: string; personaData: PersonaData; personaType?: PersonaType; status: string }
  accountId: string
  projectId: string
  onBack: () => void
  onUpdate: (data: Partial<PersonaData>) => void
  onSynthesizeProblem: (jtbdId: string) => void
  /**
   * Launch the existing workshop wizard for guided discovery.
   * - pass a numeric `jtbdTarget` to refine an existing JTBD at that index
   * - pass `'new'` to have the wizard create a fresh JTBD atomically on mount
   * - omit to run the full-persona wizard (used from the workshop landing page)
   */
  onOpenWorkshop?: (sessionId: string, jtbdTarget?: number | 'new') => void
}

const DECISION_POWERS: DecisionPower[] = ['buyer', 'user', 'champion', 'influencer', 'gatekeeper']

function computeInitials(name: string, role: string): string {
  if (name) return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
  if (role) return role[0].toUpperCase()
  return '?'
}

export function PersonaCardDetail({ session, accountId, projectId, onBack, onUpdate, onSynthesizeProblem, onOpenWorkshop }: Props) {
  const data = migratePersonaData(session.personaData)
  const [newTool, setNewTool] = useState('')
  const [newConstraint, setNewConstraint] = useState('')

  const isBuyer = session.personaType === 'buyer'
  const jtbds = data.jtbds || []
  const photo = data.photo
  const initials = computeInitials(data.name, data.role)
  const currentTools = data.currentTools || []
  const constraints = data.constraints || []
  const totalPains = jtbds.reduce((s, j) => s + j.pains.length, 0)
  const totalGains = jtbds.reduce((s, j) => s + j.gains.length, 0)

  const handleUpdateJtbd = (i: number, jtbd: JtbdData) => {
    const next = [...jtbds]
    next[i] = jtbd
    onUpdate({ jtbds: next })
  }

  const handleDeleteJtbd = (i: number) => {
    onUpdate({ jtbds: jtbds.filter((_, idx) => idx !== i) })
  }

  const handleAddBlankJtbd = () => {
    onUpdate({ jtbds: [...jtbds, { id: `jtbd-${Date.now()}`, title: '', functional: '', pains: [], gains: [] }] })
  }

  const handleAddGuidedJtbd = () => {
    if (!onOpenWorkshop) return
    onOpenWorkshop(session.id, 'new')
  }

  const handlePhotoChange = (dataUrl: string | undefined) => {
    onUpdate({ photo: dataUrl })
  }

  const addTool = () => {
    const v = newTool.trim()
    if (!v) return
    onUpdate({ currentTools: [...currentTools, v] })
    setNewTool('')
  }

  const addConstraint = () => {
    const v = newConstraint.trim()
    if (!v) return
    onUpdate({ constraints: [...constraints, v] })
    setNewConstraint('')
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-[1280px] px-8 py-6">
        <button
          onClick={onBack}
          className="mb-5 flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text-secondary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to personas
        </button>

        <div className="grid gap-x-14 gap-y-8 lg:grid-cols-[minmax(0,1fr)_280px]">

          {/* ============================================================
              MAIN COLUMN
              ============================================================ */}
          <article className="min-w-0">

            {/* Hero — identity */}
            <header className="mb-4 grid grid-cols-[64px_1fr_max-content] items-center gap-4">
              <PersonaAvatarUploader
                photo={photo}
                initials={initials}
                isBuyer={isBuyer}
                name={data.name}
                onChange={handlePhotoChange}
              />

              <div className="min-w-0">
                <div className="mb-0.5 text-[11px] font-medium uppercase tracking-[0.1em] text-market">
                  Market · Persona
                </div>
                <input
                  type="text"
                  value={data.name}
                  placeholder="Persona name"
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  className="-ml-1.5 w-full rounded px-1.5 py-0.5 font-display text-[26px] font-semibold leading-tight tracking-tight transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:text-text-muted"
                />
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-text-secondary">
                  <input
                    type="text"
                    value={data.role}
                    placeholder="e.g. Chief Product Officer at a 200-person SaaS company"
                    onChange={(e) => onUpdate({ role: e.target.value })}
                    className="-ml-1.5 min-w-[260px] flex-1 rounded px-1.5 py-0.5 transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:text-text-muted"
                  />
                  {data.archetype && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-warm-300" />
                      <input
                        type="text"
                        value={data.archetype}
                        placeholder="Archetype"
                        onChange={(e) => onUpdate({ archetype: e.target.value })}
                        className={cn(
                          'rounded px-1.5 py-0.5 italic transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none',
                          isBuyer ? 'text-business' : 'text-market',
                        )}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1.5">
                <PersonaTypeSelect
                  isBuyer={isBuyer}
                  decisionPower={data.decisionPower}
                  onChange={(value) => onUpdate({ decisionPower: value })}
                />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-warm-100 px-2.5 py-1 text-[11px] text-text-secondary">
                  <span className="h-1.5 w-1.5 rounded-full bg-market" />
                  {session.status === 'completed' ? 'Complete' : 'In progress'}
                </span>
              </div>
            </header>

            {/* Archetype suggest helper if missing */}
            {data.role && !data.archetype && (
              <div className="-mt-2 mb-4 ml-[80px]">
                <AiSuggestionChips
                  accountId={accountId} projectId={projectId} sessionId={session.id}
                  phaseId="identity" label="Suggest archetype"
                  onAccept={(text) => onUpdate({ archetype: text })}
                />
              </div>
            )}

            {/* Quote / context block */}
            <div className="relative mb-6 border-l-2 border-market py-1 pl-5">
              <textarea
                value={data.quote || ''}
                placeholder="Capture a real quote from this persona, in their own words."
                onChange={(e) => onUpdate({ quote: e.target.value })}
                rows={data.quote && data.quote.length > 120 ? 3 : 2}
                className="w-full resize-none rounded bg-transparent px-1.5 py-0.5 font-display text-[19px] italic leading-snug text-text transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:font-display placeholder:text-text-muted"
              />
              <div className="mt-1.5 flex items-center gap-3 pl-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">
                <span>From discovery and customer notes</span>
              </div>
            </div>

            {/* JTBD list */}
            <section className="border-t border-border-subtle pt-4">
              {jtbds.length === 0 ? (
                <>
                  <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                    Jobs to be done
                  </h2>
                  <div className="flex flex-col items-center gap-3 py-10 text-center">
                    <p className="text-sm text-text-muted">No jobs defined yet.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddBlankJtbd}
                        className="inline-flex items-center gap-1.5 rounded-md border border-warm-300 px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-market/40 hover:text-market"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add blank JTBD
                      </button>
                      {onOpenWorkshop && (
                        <button
                          onClick={handleAddGuidedJtbd}
                          className="inline-flex items-center gap-1.5 rounded-md bg-market px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-market-dark"
                        >
                          <Sparkles className="h-3.5 w-3.5" /> Guided setup
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {jtbds.length > 1 ? (
                    <JtbdNav jtbds={jtbds} totalPains={totalPains} totalGains={totalGains} />
                  ) : (
                    <div className="mb-3 flex items-baseline justify-between">
                      <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                        Job to be done
                      </h2>
                      <span className="text-[11px] tabular-nums text-text-muted">
                        {totalPains} pains · {totalGains} outcomes
                      </span>
                    </div>
                  )}

                  <div className="divide-y divide-border-subtle">
                    {jtbds.map((j, i) => (
                      <div
                        key={j.id}
                        id={`jtbd-${i}`}
                        className="py-5 first:pt-2 scroll-mt-24"
                      >
                        <JtbdSectionEditor
                          jtbd={j}
                          index={i}
                          accountId={accountId}
                          projectId={projectId}
                          sessionId={session.id}
                          onUpdate={(updated) => handleUpdateJtbd(i, updated)}
                          onDelete={() => handleDeleteJtbd(i)}
                          onSynthesizeProblem={onSynthesizeProblem}
                          onRefineInWorkshop={
                            onOpenWorkshop ? () => onOpenWorkshop(session.id, i) : undefined
                          }
                        />
                      </div>
                    ))}
                    <div className="flex items-center gap-3 pt-4">
                      <button
                        onClick={handleAddBlankJtbd}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add JTBD
                      </button>
                      {onOpenWorkshop && (
                        <button
                          onClick={handleAddGuidedJtbd}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
                          title="Add a new JTBD using the guided workshop"
                        >
                          <Sparkles className="h-3.5 w-3.5" /> Guided setup
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </section>

          </article>

          {/* ============================================================
              RAIL — context, tools, constraints
              ============================================================ */}
          <PersonaDetailRail
            data={data}
            accountId={accountId}
            projectId={projectId}
            sessionId={session.id}
            currentTools={currentTools}
            constraints={constraints}
            newTool={newTool}
            newConstraint={newConstraint}
            onSetNewTool={setNewTool}
            onSetNewConstraint={setNewConstraint}
            onAddTool={addTool}
            onAddConstraint={addConstraint}
            onUpdate={onUpdate}
          />

        </div>
      </div>
    </div>
  )
}

/* ==========================================================================
   JTBD nav — sticky tab strip shown when more than one JTBD exists.
   Tracks the active section via IntersectionObserver and lets users jump
   between jobs without scrolling the page manually.
   ========================================================================== */
interface JtbdNavProps {
  jtbds: JtbdData[]
  totalPains: number
  totalGains: number
}

function JtbdNav({ jtbds, totalPains, totalGains }: JtbdNavProps) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const sections = jtbds
      .map((_, i) => document.getElementById(`jtbd-${i}`))
      .filter((el): el is HTMLElement => el !== null)
    if (sections.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible) {
          const idx = Number(visible.target.id.replace('jtbd-', ''))
          if (!Number.isNaN(idx)) setActiveIndex(idx)
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    )
    sections.forEach(s => observer.observe(s))
    return () => observer.disconnect()
  }, [jtbds.length])

  return (
    <nav
      aria-label="Jobs to be done"
      className="sticky top-0 z-10 -mx-2 mb-3 border-b border-border-subtle bg-bg/90 px-2 pb-2 pt-2 backdrop-blur-sm"
    >
      <div className="mb-1.5 flex items-baseline justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
          Jobs to be done
        </h2>
        <span className="text-[11px] tabular-nums text-text-muted">
          {jtbds.length} jobs · {totalPains} pains · {totalGains} outcomes
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {jtbds.map((j, i) => {
          const isActive = i === activeIndex
          const title = j.title || `Job ${i + 1}`
          const truncated = title.length > 38 ? `${title.substring(0, 38)}…` : title
          return (
            <a
              key={j.id}
              href={`#jtbd-${i}`}
              className={cn(
                'group inline-flex max-w-[260px] items-center gap-2 rounded-md px-2 py-1.5 text-[12px] transition-colors',
                isActive
                  ? 'bg-warm-100 text-text'
                  : 'text-text-secondary hover:bg-warm-100 hover:text-text',
              )}
            >
              <span className={cn(
                'rounded px-1.5 py-0.5 font-display text-[10.5px] font-medium tabular-nums',
                isActive ? 'bg-market/15 text-market' : 'bg-warm-100 text-text-muted group-hover:bg-warm-200',
              )}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="truncate">{truncated}</span>
              <span className="shrink-0 text-[10px] tabular-nums text-text-muted">
                {j.pains.length}p · {j.gains.length}g
              </span>
            </a>
          )
        })}
      </div>
    </nav>
  )
}

/* ==========================================================================
   Persona type select — combines personaType + decisionPower into one chip
   styled control. The visible label is the decisionPower value (or 'Buyer' /
   'User' as fallback when decisionPower is empty).
   ========================================================================== */
interface PersonaTypeSelectProps {
  isBuyer: boolean
  decisionPower: DecisionPower | '' | undefined
  onChange: (value: DecisionPower | '') => void
}

function PersonaTypeSelect({ isBuyer, decisionPower, onChange }: PersonaTypeSelectProps) {
  const tone = isBuyer
    ? 'bg-business/10 text-business'
    : 'bg-market/10 text-market'

  return (
    <div className={cn('relative inline-flex items-center rounded-full', tone)}>
      <span className="ml-2.5 mr-1 h-1.5 w-1.5 rounded-full bg-current opacity-50" />
      <select
        value={decisionPower || ''}
        onChange={(e) => onChange(e.target.value as DecisionPower | '')}
        className="cursor-pointer appearance-none rounded-full bg-transparent py-1 pl-0 pr-7 text-[12px] font-medium capitalize outline-none"
      >
        <option value="">{isBuyer ? 'Buyer' : 'User'}</option>
        {DECISION_POWERS.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 h-3 w-3 opacity-60" />
    </div>
  )
}

/* ==========================================================================
   Right rail
   ========================================================================== */
interface RailProps {
  data: PersonaData
  accountId: string
  projectId: string
  sessionId: string
  currentTools: string[]
  constraints: string[]
  newTool: string
  newConstraint: string
  onSetNewTool: (value: string) => void
  onSetNewConstraint: (value: string) => void
  onAddTool: () => void
  onAddConstraint: () => void
  onUpdate: (data: Partial<PersonaData>) => void
}

function PersonaDetailRail({
  data, accountId, projectId, sessionId,
  currentTools, constraints, newTool, newConstraint,
  onSetNewTool, onSetNewConstraint, onAddTool, onAddConstraint, onUpdate,
}: RailProps) {
  return (
    <aside className="grid gap-5 self-start lg:sticky lg:top-4">

      {/* Context */}
      <div>
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">Context</h4>
        <textarea
          value={data.context || ''}
          placeholder="Their day, responsibilities, environment…"
          onChange={(e) => onUpdate({ context: e.target.value })}
          rows={data.context && data.context.length > 80 ? 4 : 3}
          className="w-full resize-none rounded bg-transparent px-1.5 py-1 text-[13px] leading-relaxed text-text-secondary transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:text-text-muted"
        />
        {data.role && !data.context && (
          <AiSuggestionChips
            accountId={accountId} projectId={projectId} sessionId={sessionId}
            phaseId="identity" label="Suggest"
            onAccept={(text) => onUpdate({ context: text })}
          />
        )}
      </div>

      {/* Current tools */}
      <div>
        <h4 className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
          Current tools
          {currentTools.length > 0 && <span className="font-normal normal-case tracking-normal text-text-muted">{currentTools.length}</span>}
        </h4>
        <div className="mb-1.5 flex flex-wrap gap-1">
          {currentTools.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-md bg-warm-100 px-2 py-1 text-[12px] text-text">
              {t}
              <button
                type="button"
                onClick={() => onUpdate({ currentTools: currentTools.filter((_, idx) => idx !== i) })}
                className="rounded p-0.5 text-text-muted transition-colors hover:bg-error/10 hover:text-error"
                aria-label={`Remove ${t}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-text-muted">
          <Plus className="h-3 w-3 shrink-0" />
          <input
            type="text"
            value={newTool}
            onChange={(e) => onSetNewTool(e.target.value)}
            placeholder="Add tool…"
            onKeyDown={(e) => e.key === 'Enter' && onAddTool()}
            onBlur={onAddTool}
            className="flex-1 rounded bg-transparent px-1 py-0.5 text-text outline-none transition-colors focus:bg-warm-100 placeholder:text-text-muted"
          />
        </div>
        {data.role && (
          <AiSuggestionChips
            accountId={accountId} projectId={projectId} sessionId={sessionId}
            phaseId="tools" label="Suggest tools"
            onAccept={(text) => onUpdate({ currentTools: [...currentTools, text] })}
          />
        )}
      </div>

      {/* Constraints */}
      <div>
        <h4 className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
          Constraints
          {constraints.length > 0 && <span className="font-normal normal-case tracking-normal text-text-muted">{constraints.length}</span>}
        </h4>
        <div className="mb-1.5 flex flex-wrap gap-1">
          {constraints.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-md bg-warm-100 px-2 py-1 text-[12px] text-text">
              {c}
              <button
                type="button"
                onClick={() => onUpdate({ constraints: constraints.filter((_, idx) => idx !== i) })}
                className="rounded p-0.5 text-text-muted transition-colors hover:bg-error/10 hover:text-error"
                aria-label={`Remove ${c}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-text-muted">
          <Plus className="h-3 w-3 shrink-0" />
          <input
            type="text"
            value={newConstraint}
            onChange={(e) => onSetNewConstraint(e.target.value)}
            placeholder="Add constraint…"
            onKeyDown={(e) => e.key === 'Enter' && onAddConstraint()}
            onBlur={onAddConstraint}
            className="flex-1 rounded bg-transparent px-1 py-0.5 text-text outline-none transition-colors focus:bg-warm-100 placeholder:text-text-muted"
          />
        </div>
      </div>

    </aside>
  )
}
